import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  useTheme,
} from '@mui/material';
import React, { ChangeEvent, KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chip from '../../../../components/Chip/Chip';
import Guide from '../../../../components/Guide/Guide';
import Section from '../../../../components/Main/Section/Section';
import { CurrentViewContext } from '../../../../providers/CurrentViewProvider';
import AiSearchMonitoringService, {
  IAiSearchScheduleQuestion,
} from '../../../../services/AiSearchMonitoringService';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const QueryList = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-height: 300px;
  overflow-y: auto;
  padding: ${(props) => props.theme.spacing(1)} 0;
`;

const AiSearchGuide = () => {
  const { setCurrentView } = useContext(CurrentViewContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [country, setCountry] = useState('DE');
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [apply, setApply] = useState(false);
  const [questions, setQuestions] = useState<IAiSearchScheduleQuestion[]>([]);
  const queryInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setCurrentView(t('ai_search_guide_title'));
    document.title = `${t('app_branding')} ${t('ai_search_guide_title')}`;
  }, [setCurrentView, t]);

  const addQuestion = () => {
    const query = queryInputRef.current?.value.trim();
    if (
      !query ||
      questions.some((question) => question.query.toLowerCase() === query.toLowerCase())
    )
      return;
    setQuestions((current) => [{ country, query }, ...current]);
    if (queryInputRef.current) queryInputRef.current.value = '';
    setApply(false);
  };

  const finish = async () => {
    const response = await AiSearchMonitoringService.startMonitoring(questions, setLoading);
    if (response) navigate(-1);
  };

  return (
    <Section>
      <Guide>
        <Box sx={{ maxWidth: 600 }}>
          <Stepper
            activeStep={currentStep}
            orientation="vertical"
          >
            <Step>
              <StepLabel>{t('select_country')}</StepLabel>
              <StepContent>
                <FormControl
                  size="small"
                  sx={{ minWidth: '284px' }}
                >
                  <InputLabel id="ai-search-country-select-label">{t('country')}</InputLabel>
                  <Select
                    labelId="ai-search-country-select-label"
                    value={country}
                    onChange={(event: SelectChangeEvent) => setCountry(event.target.value)}
                    label={t('country')}
                  >
                    <MenuItem value="DE">{t('country_germany')}</MenuItem>
                    <MenuItem value="AT">{t('country_austria')}</MenuItem>
                    <MenuItem value="CH">{t('country_switzerland')}</MenuItem>
                    <MenuItem value="NL">{t('country_netherlands')}</MenuItem>
                    <MenuItem value="UK">{t('country_united_kingdom')}</MenuItem>
                    <MenuItem value="US">{t('country_us')}</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ mt: theme.spacing(1) }}>
                  <Button
                    variant="contained"
                    onClick={() => setCurrentStep(1)}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => navigate(-1)}
                    sx={{ ml: theme.spacing(1) }}
                  >
                    {t('cancel')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
            <Step>
              <StepLabel>{t('ai_search_query_label')}</StepLabel>
              <StepContent>
                <TextField
                  disabled={loading}
                  label={t('ai_search_query_label')}
                  variant="outlined"
                  size="small"
                  inputRef={queryInputRef}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setApply(event.target.value.trim() !== '')
                  }
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter') addQuestion();
                  }}
                  sx={{ minWidth: '400px' }}
                  autoFocus
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    gap: theme.spacing(1),
                    mt: theme.spacing(1),
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => setCurrentStep(0)}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {t('back')}
                  </Button>
                  <Button
                    disabled={!apply || loading}
                    variant="contained"
                    onClick={addQuestion}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {t('ai_search_add_query')}
                  </Button>
                  <Button
                    disabled={questions.length === 0 || loading}
                    variant="contained"
                    onClick={finish}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {t('finish')}
                  </Button>
                  <Button
                    disabled={loading}
                    variant="contained"
                    color="warning"
                    onClick={() => navigate(-1)}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {t('cancel')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'flex-start',
            minWidth: '250px',
            maxWidth: '300px',
            mx: theme.spacing(6),
          }}
        >
          <QueryList>
            {questions.map((question, index) => (
              <Chip
                key={index}
                label={question.query}
                onDelete={() =>
                  setQuestions((current) => current.filter((item) => item !== question))
                }
              />
            ))}
          </QueryList>
          <Button
            disabled={questions.length === 0 || loading}
            variant="contained"
            color="error"
            onClick={() => setQuestions([])}
            sx={{ mt: 1 }}
          >
            {t('reset')}
          </Button>
        </Box>
      </Guide>
    </Section>
  );
};

export default AiSearchGuide;
